import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spinner, Table } from 'react-bootstrap';
import { useFirestore } from '../contexts/FirestoreContext';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

export default function ContractReport() {
  const { onSnapshotData } = useFirestore();

  const [salesOrders, setSalesOrders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [ordersReady, setOrdersReady] = useState(false);
  const [vehiclesReady, setVehiclesReady] = useState(false);

  useEffect(() => {
    const unsubscribeOrders = onSnapshotData('salesOrders', (data) => {
      setSalesOrders(Array.isArray(data) ? data : []);
      setOrdersReady(true);
    });

    const unsubscribeVehicles = onSnapshotData('vehicles', (data) => {
      setVehicles(Array.isArray(data) ? data : []);
      setVehiclesReady(true);
    });

    return () => {
      if (unsubscribeOrders) unsubscribeOrders();
      if (unsubscribeVehicles) unsubscribeVehicles();
    };
  }, [onSnapshotData]);

  const orderByNumber = useMemo(() => {
    const map = new Map();
    (salesOrders || []).forEach(order => {
      if (order?.strSalesOrdNbr) {
        map.set(order.strSalesOrdNbr, order);
      }
    });
    return map;
  }, [salesOrders]);

  const contracts = useMemo(() => {
    const byContract = new Map();

    (salesOrders || []).forEach(order => {
      const contractNumber = (order?.strContractNbr || '').trim();
      if (!contractNumber) return;
      if (!byContract.has(contractNumber)) {
        byContract.set(contractNumber, {
          contractNumber,
          salesOrders: [],
        });
      }
      byContract.get(contractNumber).salesOrders.push(order);
    });

    return Array.from(byContract.values()).sort((a, b) =>
      a.contractNumber.localeCompare(b.contractNumber)
    );
  }, [salesOrders]);

  const rows = useMemo(() => {
    const results = [];

    contracts.forEach(contractGroup => {
      contractGroup.salesOrders.forEach(order => {
        const relatedVehicles = (vehicles || []).filter(
          vehicle => vehicle?.strSalesOrdNbr && vehicle.strSalesOrdNbr === order.strSalesOrdNbr
        );

        if (relatedVehicles.length === 0) {
          results.push({
            key: `${contractGroup.contractNumber}-${order.strSalesOrdNbr}-none`,
            contractNumber: contractGroup.contractNumber,
            salesOrderNumber: order.strSalesOrdNbr || '—',
            vin: '—',
            year: '',
            model: '',
            poNumber: order?.strPONbr || '',
            paidDate: '',
            orderDate: formatDate(order?.dtSalesOrdDate || order?.dtSaleDate),
            deliveryDate: '',
          });
        } else {
          relatedVehicles.forEach(vehicle => {
            results.push({
              key: `${contractGroup.contractNumber}-${vehicle.id || vehicle.strVin || Math.random()}`,
              contractNumber: contractGroup.contractNumber,
              salesOrderNumber: order.strSalesOrdNbr || '—',
              vin: vehicle?.strVin || '',
              year: vehicle?.strYR || '',
              model: vehicle?.strModel || '',
              poNumber: order?.strPONbr || '',
              paidDate: formatDate(vehicle?.dtPaidDate),
              orderDate:
                formatDate(order?.dtSalesOrdDate || order?.dtSaleDate) ||
                formatDate(vehicle?.dtSaleDate),
              deliveryDate: formatDate(vehicle?.dtDelivDate),
            });
          });
        }
      });
    });

    return results.sort((a, b) => {
      const contractCompare = a.contractNumber.localeCompare(b.contractNumber);
      if (contractCompare !== 0) return contractCompare;
      const soCompare = (a.salesOrderNumber || '').localeCompare(b.salesOrderNumber || '');
      if (soCompare !== 0) return soCompare;
      return (a.vin || '').localeCompare(b.vin || '');
    });
  }, [contracts, vehicles]);

  const loading = !ordersReady || !vehiclesReady;

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Card>
      <Card.Body>
        <div className="text-center mb-3">
          <div className="fw-bold">Hardy Automotive Fleet Group</div>
          <div>Contract Report</div>
          <div className="small text-muted">
            {rows.length === 1
              ? '1 vehicle found under active contracts'
              : `${rows.length} vehicles found under active contracts`}
          </div>
        </div>

        <Table bordered responsive hover>
          <thead>
            <tr>
              <th style={{ minWidth: 140 }}>Contract #</th>
              <th style={{ minWidth: 130 }}>Sales Order #</th>
              <th style={{ minWidth: 160 }}>VIN</th>
              <th style={{ minWidth: 80 }}>Year</th>
              <th style={{ minWidth: 140 }}>Model</th>
              <th style={{ minWidth: 120 }}>PO #</th>
              <th style={{ minWidth: 120 }}>Order Date</th>
              <th style={{ minWidth: 120 }}>Paid Date</th>
              <th style={{ minWidth: 130 }}>Delivery Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-4">
                  No contracts with associated vehicles found.
                </td>
              </tr>
            ) : (
              rows.map(row => (
                <tr key={row.key}>
                  <td className="fw-semibold">{row.contractNumber}</td>
                  <td>{row.salesOrderNumber}</td>
                  <td>{row.vin}</td>
                  <td>{row.year}</td>
                  <td>{row.model}</td>
                  <td>{row.poNumber}</td>
                  <td>{row.orderDate}</td>
                  <td>{row.paidDate}</td>
                  <td>{row.deliveryDate}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}
